import { AdjustmentsHorizontalIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import { SectionTitle } from '@/components/common/SectionTitle';
import type { FcfEntitlementCards } from '@/features/decisions/decisions.api.types';
import { fmtInteger } from '@/features/decisions/utils/fcfFormat';

interface Props {
  cards: FcfEntitlementCards | undefined;
  // Region = the scenario's site. fcf-entitlement doesn't echo it yet (portfolio
  // rollup), so it's passed in; bind to resolved.site once the backend returns it.
  region: string;
  periodFrom: string;
  periodTo: string;
}

// MM/DD/YY, matching the Training Window convention — keeps Training and Test
// Window in the same date format.
const slashDate = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: '2-digit',
  timeZone: 'UTC',
});

function fmtSlashRange(from: string | null | undefined, to: string | null | undefined): string {
  if (!from || !to) return '—';
  return `${slashDate.format(new Date(`${from}T00:00:00Z`))} - ${slashDate.format(new Date(`${to}T00:00:00Z`))}`;
}

// Result Specification (backtest scope) — mirrors the wireframe's scope header.
// SKU Count + Test Window come from fcf-entitlement cards; the rest are
// hardcoded for the demo pending backend metadata.
export function ResultSpecificationPanel({ cards, periodFrom, periodTo }: Props) {
  const fields: Array<{ label: string; value: string }> = [
    { label: 'Product', value: 'Life Sciences' },
    { label: 'Region', value: 'North America' },
    { label: 'Customers', value: 'All' },
    { label: 'Grain', value: 'SKU' },
    { label: 'SKU Count', value: fmtInteger(cards?.skus_evaluated) },
    { label: 'Time Frequency', value: 'Weekly' },
    { label: 'Training Window', value: fmtSlashRange('2023-08-01', '2024-08-01') },
    { label: 'Test Window', value: fmtSlashRange(cards?.period_from ?? periodFrom, cards?.period_to ?? periodTo) },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-4">
        <SectionTitle>Result Specification</SectionTitle>
        <div className="flex items-center gap-3 text-gray-400">
          <button type="button" aria-label="Filter" className="hover:text-gray-600">
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
          </button>
          <button type="button" aria-label="Download" className="hover:text-gray-600">
            <ArrowDownTrayIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto border-t border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-500">
              {fields.map((f) => (
                <th key={f.label} className="whitespace-nowrap px-4 py-2">
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {fields.map((f) => (
                <td key={f.label} className="whitespace-nowrap px-4 py-3 text-gray-900">
                  {f.value}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
